import IconButton from 'Components/Buttons/IconButton';
import Panel from 'Components/Panel';
import { actions as readingActions } from 'ducks/deviceVerificationReadings';
import { actions as logActions } from 'ducks/logs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import icons from 'utils/ui/icons';
import useVerificationScreen from '../useVerificationScreen';
import { displayRatio, getDeltaVariation, getMeasurementFromReadingKindName, getRealtimeValue, numericValues } from '../utils';
import VerificationShell, { PanelButton, PanelHeader } from '../VerificationShell';
import ReadingsTable from './ReadingsTable';

const DeltaVerification = ({ device }) => {
    const resetForm = () => {
        setSystemValue('');
        setTechnicianValue('');
        setComments('');
    };

    const screen = useVerificationScreen(device, resetForm);
    const { currentVerification, dispatch, finalValidated, image, loading, measurements, profile } = screen;
    const { readings, readingsById, realtime, submitting, unitSymbols, verificationNumber, verificationReadings } = screen;
    const { restoreFinalFields, setDrawerOpen, setImage, setShowCameraModal } = screen;

    const { photo, primary } = profile;
    const quantity = primary[0];
    const photoRequired = photo === 'required';
    const ratio = displayRatio(getMeasurementFromReadingKindName(quantity, measurements, readings), readingsById, unitSymbols);

    const readingValues = reading => reading && { system: reading.meter_values?.[quantity], technician: reading.technician_values?.[quantity] };
    const variationFor = (reading, previous) => getDeltaVariation(reading.meter_values?.[quantity], reading.technician_values?.[quantity], readingValues(previous));

    const [systemValue, setSystemValue] = useState('');
    const [technicianValue, setTechnicianValue] = useState('');
    const [comments, setComments] = useState('');
    const [addingReading, setAddingReading] = useState(false);

    const openedVerificationRef = useRef(null);
    useEffect(() => {
        if (!currentVerification?.id || openedVerificationRef.current === currentVerification.id) {
            return;
        }
        openedVerificationRef.current = currentVerification.id;
        setAddingReading(false);
    }, [currentVerification?.id]);

    const systemOnline = useMemo(() => {
        const measurement = getMeasurementFromReadingKindName(quantity, measurements, readings);
        if (!measurement) {
            return false;
        }
        const realtimeMatch = realtime.find(rtm => rtm.id === measurement.id);
        return realtimeMatch?.value !== undefined && realtimeMatch?.value !== null;
    }, [measurements, quantity, readings, realtime]);

    const readingsCountRef = useRef(0);
    useEffect(() => {
        if (verificationReadings.length > readingsCountRef.current) {
            setAddingReading(false);
            setTechnicianValue('');
            setComments('');
            setImage(null);
            restoreFinalFields(currentVerification);
        }
        readingsCountRef.current = verificationReadings.length;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verificationReadings.length]);

    useEffect(() => {
        if (realtime.length === 0 || finalValidated) {
            return;
        }
        try {
            const measurement = getMeasurementFromReadingKindName(quantity, measurements, readings);
            if (measurement) {
                setSystemValue(getRealtimeValue(realtime, measurement, readingsById));
            }
        } catch {
            // Reading kind not found
        }
    }, [finalValidated, measurements, quantity, readings, readingsById, realtime]);

    const handleRefresh = () => {
        setSystemValue('');
        dispatch(logActions.fetchRealtime(device.id));
    };

    const handleAddReading = () => {
        setAddingReading(true);
        handleRefresh();
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
        if (!finalValidated) {
            setSystemValue('');
        }
    };

    const canSubmit = Boolean(systemValue) && Boolean(technicianValue) && (!photoRequired || Boolean(image));

    // The first reading has nothing to compare against; the report opens with the second.
    const finalReportReady = verificationReadings.length > 1;

    const canAddReading = Boolean(currentVerification) && !addingReading && !finalValidated;

    const handleValidate = () => {
        if (!currentVerification) {
            return;
        }
        dispatch(readingActions.create({
            comments,
            device_verification_id: currentVerification.id,
            meter_values: numericValues({ [quantity]: systemValue }),
            online: realtime.length > 0,
            technician_values: numericValues({ [quantity]: technicianValue }),
            ...(image ? { image_data_uri: image } : {})
        }, () => {}));
    };

    const variationValue = getDeltaVariation(systemValue, technicianValue, readingValues(verificationReadings.at(-1)));

    const nextReadingNumber = verificationReadings.length + 1;

    const rows = [
        ...verificationReadings.map((r, i) => {
            const variation = variationFor(r, verificationReadings[i - 1]);
            return {
                id: i + 1,
                readingNumber: i + 1,
                date: r.created_at ? new Date(r.created_at * 1000).toLocaleDateString() : '',
                system: r.meter_values?.[quantity] ?? '',
                technician: r.technician_values?.[quantity] ?? '',
                variation,
                incomparable: i === 0,
                image: r.image,
                comments: r.comments || '',
                online: r.online || false,
                needsPhoto: false,
                validated: true
            };
        }),
        ...(addingReading ? [{
            id: nextReadingNumber,
            readingNumber: nextReadingNumber,
            date: new Date().toLocaleString(),
            system: systemValue,
            technician: technicianValue,
            variation: variationValue,
            incomparable: verificationReadings.length === 0,
            image,
            comments,
            online: realtime.length > 0,
            needsPhoto: photoRequired,
            validated: false
        }] : [])
    ];

    const validateAction = addingReading && (
        <span style={{ paddingRight: 20 }}><PanelButton disabled={!canSubmit || submitting} onClick={handleValidate}>Validate</PanelButton></span>
    );

    return (
        <VerificationShell device={device} finalReportReady={finalReportReady} onDrawerClose={handleDrawerClose} screen={screen}>
            <div id="primary-verification" style={{ margin: '8px 0' }}>
                <Panel
                    header={
                        <PanelHeader action={validateAction}>
                            {`Primary Verification #${verificationNumber}`}
                            {!finalValidated && (
                                <IconButton icon={icons.refresh} onClick={handleRefresh} spin={loading} tooltip="Refresh values"/>
                            )}
                            {canAddReading && (
                                <IconButton icon={icons.plus} onClick={handleAddReading} tooltip="Add verification reading"/>
                            )}
                            {addingReading && (
                                <IconButton icon={['fas', 'camera']} onClick={() => setShowCameraModal(true)} tooltip="Add photo"/>
                            )}
                        </PanelHeader>
                    }
                >
                    <ReadingsTable
                        comments={comments}
                        loading={(loading || submitting) && !finalValidated}
                        onCommentsChange={setComments}
                        onDiscardImage={() => setImage(null)}
                        onSystemChange={setSystemValue}
                        onTechnicianChange={setTechnicianValue}
                        ratio={ratio}
                        rows={rows}
                        systemOnline={systemOnline}
                        systemValue={systemValue}
                        technicianValue={technicianValue}
                    />
                </Panel>
            </div>
        </VerificationShell>
    );
};

export default DeltaVerification;
